import type { WholeStoryType } from "@config";
import { useEffect, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { handleDesign } from "@/shared";

export const useLoadStory = (
  contentPointer: string | undefined,
  containerRef: React.RefObject<HTMLDivElement | null>,
  skipLoad?: boolean,
) => {
  "use no memo";
  const [story, setStory] = useState<WholeStoryType | null | undefined>(
    undefined,
  );
  // Load as soon as ANY part of the preview approaches the viewport. The old
  // 0.5 threshold meant a card straddling the fold (or one that can never be
  // 50% visible in a short container, like the chrome-extension panel) never
  // loaded at all. rootMargin preloads slightly ahead so scrolling doesn't
  // show skeletons.
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.01,
    rootMargin: "200px 0px",
  });

  useEffect(() => {
    if (!isIntersecting || story) return;
    if (skipLoad) return setStory(null);
    const getSlides = async () => {
      if (!contentPointer) {
        setStory(null);
        return;
      }
      const content = await fetch(contentPointer).then((res) => res.json());

      const isValidStructure =
        !!content.slides?.[0]?.id && !!content.slides?.[0]?.type;

      if (isValidStructure) {
        const slides = content.slides.slice(0, 2);
        setStory({
          ...content,
          slides,
        });
        handleDesign(
          content.settings.designSystemV2,
          containerRef.current as HTMLElement,
        );
      } else setStory(null);
    };
    getSlides();
  }, [isIntersecting, contentPointer, containerRef, story, skipLoad]);

  return { ref, story, isIntersecting };
};
